import React, { useState, useEffect } from 'react';
import { Typography, Box, Grid, Divider, Chip, Paper, Alert, FormControl, InputLabel, Select, MenuItem, Button } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import InfoIcon from '@mui/icons-material/Info';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  MainSection
} from '../../common/styles';
import { skillsApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import MappingSummary from './components/MappingSummary';
import EmployeeCard from './components/EmployeeCard';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { toLocalDate } from '../../../utils/dateOnly';

const EmployeeMatrixMatching = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [mappingData, setMappingData] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Get sessionId from URL params
  const sessionId = searchParams.get('sessionId');

  // Fetch available sessions on component mount
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoadingSessions(true);
        const response = await skillsApi.getAllSkillsMatrixSessions();
        // Filter to only show published sessions
        const publishedSessions = response.filter(session => session.publishedAt);
        setSessions(publishedSessions);
      } catch (error) {
        console.error('Error fetching sessions:', error);
        showAlert(error.message || 'Failed to load sessions', 'error', error);
      } finally {
        setLoadingSessions(false);
      }
    };

    fetchSessions();
  }, [showAlert]);

  // Fetch employee mapping data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await skillsApi.getEmployeeTemplateMapping(sessionId);
        setMappingData(response);
      } catch (error) {
        console.error('Error fetching employee template mapping:', error);
        showAlert(error.message || 'Failed to load individual template mapping data', 'error', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showAlert, sessionId]);

  // Filter employees with matching templates
  const mappedEmployees = mappingData?.employeeMappings?.filter(emp => emp.hasMatchingTemplate) || [];
  // Get unmapped employees
  const unmappedEmployees = mappingData?.unmatchedEmployees || [];

  // Get the employees to display based on the selected filter
  const getFilteredEmployees = () => {
    switch (selectedFilter) {
      case 'mapped':
        return { 
          mapped: mappedEmployees,
          unmapped: [] 
        };
      case 'unmapped':
        return { 
          mapped: [],
          unmapped: unmappedEmployees 
        };
      case 'all':
      default:
        return { 
          mapped: mappedEmployees,
          unmapped: unmappedEmployees 
        };
    }
  };

  const { mapped, unmapped } = getFilteredEmployees();

  // Handle filter change from the MappingSummary component
  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  // Clear filter handler
  const handleClearFilter = () => {
    setSelectedFilter(null);
  };

  // Handle session selection
  const handleSessionChange = (event) => {
    const newSessionId = event.target.value;
    if (newSessionId) {
      setSearchParams({ sessionId: newSessionId });
    } else {
      setSearchParams({});
    }
  };

  // Clear session selection (view all)
  const handleClearSession = () => {
    setSearchParams({});
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/skills-matrix')}>
            <FaArrowLeft /> Back to Skills Matrix
          </BackButton>
          <Title>
            Employee Matrix Matching
            {mappingData?.session && (
              <Typography
                component="span"
                sx={{
                  ml: 2,
                  fontSize: '0.9rem',
                  color: 'text.secondary',
                  fontWeight: 'normal'
                }}
              >
                ({mappingData.session.name})
              </Typography>
            )}
          </Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainSection>
          {/* Session Selector */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <FormControl sx={{ minWidth: 300 }} size="small">
              <InputLabel id="session-select-label">Filter by Session</InputLabel>
              <Select
                labelId="session-select-label"
                id="session-select"
                value={sessionId || ''}
                label="Filter by Session"
                onChange={handleSessionChange}
                disabled={loadingSessions}
              >
                <MenuItem value="">
                  <em>All Templates (No Session Filter)</em>
                </MenuItem>
                {sessions.map((session) => (
                  <MenuItem key={session.id} value={session.id}>
                    {session.name} (v{session.version})
                    {session.reviewType && ` - ${session.reviewType}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {sessionId && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearSession}
              >
                Clear Session
              </Button>
            )}
          </Box>

          {loading ? (
            <Box sx={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LoadingSpinner message="Loading individual matrix mapping data..." />
            </Box>
          ) : mappingData && mappingData.totalEmployees > 0 ? (
            <>
              {/* Session Information */}
              {mappingData.session && (
                <Alert
                  icon={<InfoIcon />}
                  severity="info"
                  sx={{ mb: 3 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Session: {mappingData.session.name}
                  </Typography>
                  {mappingData.session.description && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {mappingData.session.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <Chip
                      size="small"
                      label={`Version ${mappingData.session.version}`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`Review Type: ${mappingData.session.reviewType}`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`${mappingData.session.templateCount} Templates`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`${mappingData.session.clusterCount} Clusters`}
                      color="primary"
                      variant="outlined"
                    />
                    {mappingData.session.publishedAt && (
                      <Chip
                        size="small"
                        label={`Published: ${toLocalDate(mappingData.session.publishedAt)?.toLocaleDateString()}`}
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  {mappingData.session.clusters && mappingData.session.clusters.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Clusters: {mappingData.session.clusters.map(c => c.name).join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Alert>
              )}

              <MappingSummary 
                totalEmployees={mappingData.totalEmployees}
                mappedEmployees={mappingData.mappedEmployees}
                unmappedEmployees={mappingData.unmappedEmployees}
                selectedFilter={selectedFilter}
                onFilterChange={handleFilterChange}
              />
              
              {selectedFilter && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Active filter:
                  </Typography>
                  <Chip
                    label={
                      selectedFilter === 'mapped'
                        ? 'Mapped Individuals'
                        : selectedFilter === 'unmapped'
                          ? 'Unmapped Individuals'
                          : 'All Individuals'
                    } 
                    size="small" 
                    onDelete={handleClearFilter}
                    color={
                      selectedFilter === 'mapped' 
                        ? 'success' 
                        : selectedFilter === 'unmapped' 
                          ? 'error' 
                          : 'default'
                    }
                  />
                </Box>
              )}
              
              {mapped.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                    Individuals with Matching Templates
                  </Typography>
                  
                  <Grid 
                    container 
                    spacing={3}
                    sx={{
                      width: '100%',
                      margin: '0 auto',
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(3, 1fr)',
                        xl: 'repeat(3, 1fr)'
                      },
                      gap: '24px',
                      '& .MuiGrid-item': {
                        padding: '0',
                        width: '100%'
                      }
                    }}
                  >
                    {mapped.map((employee) => (
                      <Grid item key={employee.employeeId}>
                        <EmployeeCard employee={employee} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
              
              {unmapped.length > 0 && (
                <>
                  {mapped.length > 0 && <Divider sx={{ my: 4 }} />}
                  
                  <Typography variant="h6" sx={{ mt: 4, mb: 2, color: '#F44336' }}>
                    Individuals Without Matching Templates
                  </Typography>
                  
                  <Grid 
                    container 
                    spacing={3}
                    sx={{
                      width: '100%',
                      margin: '0 auto',
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(3, 1fr)',
                        xl: 'repeat(3, 1fr)'
                      },
                      gap: '24px',
                      '& .MuiGrid-item': {
                        padding: '0',
                        width: '100%'
                      }
                    }}
                  >
                    {unmapped.map((employee) => (
                      <Grid item key={employee.employeeId}>
                        <EmployeeCard employee={employee} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {mapped.length === 0 && unmapped.length === 0 && (
                <Box sx={{ mt: 4 }}>
                  <EmptyState 
                    title="No individuals match the selected filter" 
                    description="Try selecting a different filter option from the summary cards above."
                  />
                </Box>
              )}
            </>
          ) : (
            <EmptyState 
              title="No Individual Matrix Mapping Data Available"
              description="There are no individuals or matrix templates to display. Please add individuals and create skills matrix templates to see the mapping."
            />
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default EmployeeMatrixMatching; 